/**
 * <volier-combo-box>: build a Volier combo (snippets/volier-combo-box.liquid).
 * Used inside the product page sheet (mode "sheet": the aroma list replaces the combos) and on
 * /pages/arma-tu-promo (mode "page": the aroma list opens in its own bottom sheet).
 * Cart: the promo product's variant with one line item property per bottle, e.g.
 * "Perfume 1 (100 ml): Marsella", "Regalo (30 ml): Sorpresa Volier (lo elegimos por ti)".
 * Dispatches "volier-combo:select" with { promo } when the selected combo changes.
 *
 * Less thinking for the shopper: tapping a bottle shows only two options with the reason for each
 * (the anchor perfume's pairings, guide "r" / "w"; Mujer / Hombre show our two favourites, config
 * "picks", with their phrase). A small "Arma tu combo a tu gusto" link unfolds the whole catalogue.
 */
const GIFT_SURPRISE = 'Sorpresa Volier (lo elegimos por ti)';
const SURPRISE = 'surprise';
const SURPRISE_ICON =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10h16v10H4zM3 7h18v3H3zM12 7v13M12 7c-1.5-3-5-3.5-5-1.2C7 7 9.5 7 12 7zm0 0c1.5-3 5-3.5 5-1.2C17 7 14.5 7 12 7z"/></svg>';

class VolierComboBox extends HTMLElement {
  #choices = {};
  #active = null;
  #filterValue = 'Mujer';
  #manual = false;

  connectedCallback() {
    const config = JSON.parse(this.querySelector('[data-combo-config]').textContent);
    this.mode = this.dataset.mode;
    this.fixed = config.fixed;
    this.fixedName = config.fixedName;
    this.guide = config.guide || {};
    this.picks = config.picks || {};
    this.aromas = config.aromas.map((aroma) => ({ ...aroma, ...this.#describe(aroma) }));
    this.thumbs = Object.fromEntries(
      [...this.querySelectorAll('template[data-thumb]')].map((template) => [template.dataset.thumb, template])
    );

    this.body = this.querySelector('[data-body]');
    // In the product-page sheet the footer is not pinned: it sits at the end of the content
    this.foot = this.querySelector('.vcb__foot');
    if (this.mode === 'sheet' && this.body && this.foot) this.body.appendChild(this.foot);
    this.views = Object.fromEntries([...this.querySelectorAll('[data-view]')].map((view) => [view.dataset.view, view]));
    this.combos = [...this.querySelectorAll('[data-combo]')];
    this.list = this.querySelector('[data-aromas]');
    this.progress = this.querySelector('[data-progress]');
    this.addButton = this.querySelector('[data-add]');
    this.addError = this.querySelector('[data-add-error]');
    this.suggest = this.querySelector('[data-suggest]');
    this.pickerSheet = this.querySelector('[data-picker-sheet]');
    this.recLabel = this.querySelector('[data-rec-label]');
    this.manualButton = this.querySelector('[data-manual]');
    this.manualButton?.addEventListener('click', () => {
      this.#manual = true;
      this.#renderPicker();
      this.#scrollPickerTop();
    });
    // From the whole catalogue back to the two recommended (A / B)
    this.backButton = this.querySelector('[data-unmanual]');
    this.backButton?.addEventListener('click', () => {
      this.#manual = false;
      this.#renderPicker();
      this.#scrollPickerTop();
    });

    this.combos.forEach((combo) => {
      this.#choices[combo.dataset.combo] = [];
      combo.querySelectorAll('[data-slot]').forEach((slot, i) => {
        slot.dataset.index = i;
        this.#paintSlot(slot, null);
        slot.addEventListener('click', () => this.openPicker(combo, slot));
      });
      combo.querySelector('input').addEventListener('change', () => this.select(combo.dataset.combo));
    });

    this.querySelectorAll('[data-filter]').forEach((button) =>
      button.addEventListener('click', () => this.#filter(button.dataset.filter))
    );
    this.querySelector('[data-back]').addEventListener('click', () => this.closePicker());
    // While aromas are missing the button opens the next empty bottle; once complete it adds to cart
    this.addButton.addEventListener('click', () => {
      const next = this.#nextEmpty();
      if (next) this.openPicker(this.combo, next);
      else this.addToCart();
    });

    if (this.pickerSheet) {
      this.querySelector('[data-picker-close]').addEventListener('click', () => this.closePicker());
      // Only a click on the backdrop closes it (keyboard clicks have no coordinates)
      this.pickerSheet.addEventListener('click', (event) => {
        if (event.target === this.pickerSheet) this.closePicker();
      });
      this.pickerSheet.addEventListener('close', () => this.#returnFocus());
    }

    // Builder page deep link: ?promo=full|semifull|mix&perfume=<handle>
    const params = new URLSearchParams(window.location.search);
    const promo = this.mode === 'page' ? params.get('promo') : null;
    this.select(this.combos.some((combo) => combo.dataset.combo === promo) ? promo : this.combos[0]?.dataset.combo, {
      preset: this.mode === 'page' ? params.get('perfume') : null,
    });
  }

  /** The two aromas to show for an anchor perfume: its pairings ("all") or our favourites for her / him */
  #recsFor(anchor, filter) {
    const pairing = anchor ? this.guide[anchor] : null;
    const handles = filter === 'all' ? pairing?.r || [] : this.picks[filter] || [];
    return handles
      .filter((handle) => handle !== anchor)
      .map((handle) => this.aromas.find((aroma) => aroma.handle === handle))
      .filter(Boolean)
      .slice(0, 2)
      .map((aroma) => ({ aroma, why: (filter === 'all' && pairing?.w?.[aroma.handle]) || aroma.phrase || '' }));
  }

  /** Line 1 (name · hour · gender) and line 2 (family · moment) of an aroma */
  #describe(aroma) {
    const info = this.guide[aroma.handle] || {};
    const gender = info.g || aroma.gender || '';
    return {
      gender,
      title: [aroma.name, aroma.hour, gender].filter(Boolean).join(' · '),
      meta: [info.f, info.m].filter(Boolean).join(' · '),
      personality: info.p || '',
    };
  }

  get combo() {
    return this.combos.find((combo) => combo.querySelector('input').checked) || this.combos[0];
  }

  /** Select a combo card; on the builder page, a perfume can come preselected in bottle 1 */
  select(promo, { preset } = {}) {
    const combo = this.combos.find((item) => item.dataset.combo === promo) || this.combos[0];
    if (!combo) return;
    combo.querySelector('input').checked = true;
    if (preset) {
      const aroma = this.aromas.find((item) => item.handle === preset);
      const slot = combo.querySelector('[data-slot]:not([data-gift])');
      if (aroma && slot) {
        this.#choices[combo.dataset.combo][slot.dataset.index] = aroma;
        this.#paintSlot(slot, aroma);
      }
    }
    this.#refresh();
    this.dispatchEvent(new CustomEvent('volier-combo:select', { bubbles: true, detail: { promo: combo.dataset.combo } }));
  }

  /** The suggestion follows the page perfume, or else the first aroma already in this combo */
  #anchor(combo) {
    if (this.fixed) return this.fixed;
    const first = this.#choices[combo.dataset.combo].find((choice) => choice && choice !== SURPRISE);
    return first?.handle || null;
  }

  openPicker(combo, slot) {
    if (combo !== this.combo) this.select(combo.dataset.combo);
    this.#active = { combo, slot };
    this.querySelector('[data-picker-slot]').textContent = slot.dataset.title;

    const anchor = this.#anchor(combo);
    const own = this.#choices[combo.dataset.combo][slot.dataset.index];
    // Bottle 1 on the builder page is the anchor itself: no suggestion there
    const pairingAnchor = anchor && !(own && own.handle === anchor) ? anchor : null;
    this.#picker = { isGift: slot.hasAttribute('data-gift'), own, anchor: pairingAnchor };
    this.#manual = false;
    // Only Mujer / Hombre (no "Todos"): start on the gender of the aroma already chosen or of the page perfume
    const base = own && own !== SURPRISE ? own : this.aromas.find((aroma) => aroma.handle === (anchor || this.fixed));
    const gender = (base?.gender || '').toLowerCase();
    const isMan = gender.includes('hombre') || gender.includes('masculin');
    const isWoman = gender.includes('mujer') || gender.includes('femenin');
    this.#filter(isMan && !isWoman ? 'Hombre' : isWoman && !isMan ? 'Mujer' : this.#filterValue);

    if (this.pickerSheet) {
      this.pickerSheet.showModal();
      this.pickerSheet.querySelector('[data-picker-body]').scrollTop = 0;
    } else {
      this.#show('aromas');
    }
  }

  closePicker() {
    if (this.pickerSheet) {
      if (this.pickerSheet.open) this.pickerSheet.close();
    } else {
      this.#show('combos');
      this.#returnFocus();
    }
  }

  /** Back in the sheet's combos view (also when the product page dialog closes) */
  reset() {
    if (!this.pickerSheet) this.#show('combos');
    this.#active = null;
  }

  #show(name) {
    Object.entries(this.views).forEach(([key, view]) => (view.hidden = key !== name));
    if (this.mode === 'sheet' && this.foot) this.foot.hidden = name !== 'combos';
    if (this.body) this.body.scrollTop = 0;
  }

  #returnFocus() {
    this.#active?.slot.focus({ preventScroll: this.mode === 'sheet' });
    this.#active = null;
  }

  /** Recommended aromas first, with their tag; the gift bottle starts with the surprise option */
  #renderList(isGift, current, recommended) {
    const ordered = [
      ...recommended.map((handle) => this.aromas.find((aroma) => aroma.handle === handle)).filter(Boolean),
      ...this.aromas.filter((aroma) => !recommended.includes(aroma.handle)),
    ];
    const rows = ordered.map((aroma) =>
      this.#row({
        value: aroma.handle,
        title: aroma.title,
        meta: aroma.meta,
        personality: aroma.personality,
        gender: aroma.gender,
        tag: recommended.includes(aroma.handle) ? 'Recomendado' : '',
        selected: current?.handle === aroma.handle,
      })
    );
    if (isGift) {
      rows.unshift(
        this.#row({
          value: SURPRISE,
          title: 'Que Volier me sorprenda',
          meta: 'Elegimos uno por ti',
          gender: 'all',
          surprise: true,
          selected: current === SURPRISE,
        })
      );
    }
    this.list.replaceChildren(...rows);
  }

  #row({ value, title, meta, personality = '', gender, tag = '', why = '', selected = false, surprise = false }) {
    const li = document.createElement('li');
    li.dataset.gender = gender;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `vo__aroma${surprise ? ' vo__aroma--surprise' : ''}`;
    button.setAttribute('aria-pressed', String(selected));
    button.innerHTML = `
      <span class="vo__aroma-photo${surprise ? ' vo__aroma-photo--surprise' : ''}" aria-hidden="true"></span>
      <span class="vo__aroma-text">
        <span class="vo__aroma-name"></span>
        <span class="vo__aroma-meta"></span>
        ${personality ? '<span class="vo__aroma-personality"></span>' : ''}
        ${tag ? '<span class="vo__aroma-tag"></span>' : ''}
        ${why ? '<span class="vo__aroma-why"></span>' : ''}
      </span>
      <span class="vo__aroma-check" aria-hidden="true"><svg viewBox="0 0 20 20"><path d="M5 10.5l3.2 3L15 6.5"/></svg></span>`;
    const photo = button.querySelector('.vo__aroma-photo');
    if (surprise) photo.innerHTML = SURPRISE_ICON;
    else if (this.thumbs[value]) photo.append(this.thumbs[value].content.cloneNode(true));
    button.querySelector('.vo__aroma-name').textContent = title;
    button.querySelector('.vo__aroma-meta').textContent = meta;
    if (personality) button.querySelector('.vo__aroma-personality').textContent = personality;
    if (tag) button.querySelector('.vo__aroma-tag').textContent = tag;
    if (why) button.querySelector('.vo__aroma-why').textContent = why;
    button.addEventListener('click', () => this.#choose(value));
    li.append(button);
    return li;
  }

  #picker = { isGift: false, own: null, anchor: null };

  /** Two recommended aromas with their reason, or the whole list after "Armar a mi gusto" */
  #renderPicker() {
    const { isGift, own, anchor } = this.#picker;
    const filter = this.#filterValue;
    const available = this.#recsFor(anchor, filter);
    const recs = this.#manual ? [] : available;
    const anchorName = this.aromas.find((aroma) => aroma.handle === anchor)?.name || this.fixedName;
    this.suggest.hidden = true;
    // In the whole catalogue there is always a way back to the recommendation
    this.backButton.hidden = !(this.#manual && available.length);
    // Why these two: the perfect complement of the perfume already in the combo, chosen by Volier
    const why = anchorName
      ? `El complemento perfecto para tu ${anchorName}, elegido por Volier.`
      : 'El complemento perfecto para tu combo, elegido por Volier.';
    this.querySelector('[data-unmanual-label]').textContent = 'Volver a la recomendación premium';
    const backNote = this.querySelector('[data-unmanual-note]');
    if (backNote) {
      backNote.textContent = why;
      backNote.hidden = this.backButton.hidden;
    }
    const recNote = this.querySelector('[data-rec-note]');
    if (recNote) recNote.hidden = !recs.length;

    if (recs.length) {
      this.recLabel.textContent = 'Recomendación premium';
      if (recNote) recNote.textContent = why;
      this.recLabel.hidden = false;
      this.manualButton.hidden = false;
      this.list.replaceChildren(
        ...recs.map(({ aroma, why }) =>
          this.#row({
            value: aroma.handle,
            title: aroma.title,
            meta: aroma.meta,
            gender: 'all',
            why,
            selected: own?.handle === aroma.handle,
          })
        )
      );
      return;
    }

    this.recLabel.hidden = true;
    this.manualButton.hidden = true;
    const pairing = anchor ? this.guide[anchor] : null;
    if (pairing?.s) {
      this.suggest.textContent = pairing.s;
      this.suggest.hidden = false;
    }
    this.#renderList(isGift, own, pairing?.r || []);
    this.#hideByGender(filter);
  }

  #scrollPickerTop() {
    const scroller = this.pickerSheet?.querySelector('[data-picker-body]') || this.body;
    if (scroller) scroller.scrollTop = 0;
  }

  #filter(value) {
    this.#filterValue = value;
    this.querySelectorAll('[data-filter]').forEach((button) =>
      button.setAttribute('aria-pressed', String(button.dataset.filter === value))
    );
    this.#renderPicker();
  }

  #hideByGender(value) {
    [...this.list.children].forEach((li) => {
      const gender = li.dataset.gender;
      li.hidden = value !== 'all' && gender !== 'all' && !gender.toLowerCase().includes(value.toLowerCase());
    });
  }

  #choose(value) {
    if (!this.#active) return;
    const { combo, slot } = this.#active;
    const choice = value === SURPRISE ? SURPRISE : this.aromas.find((aroma) => aroma.handle === value);
    this.#choices[combo.dataset.combo][slot.dataset.index] = choice;
    this.#paintSlot(slot, choice);
    this.#refresh();
    this.closePicker();
  }

  #paintSlot(slot, choice) {
    const name = choice === SURPRISE ? 'Sorpresa Volier' : choice?.name;
    slot.classList.toggle('is-filled', Boolean(name));
    // Chosen aroma: its product photo replaces the dotted silhouette (the surprise stays a black bottle)
    slot.querySelector('.vo__piece-photo')?.remove();
    const thumb = choice && choice !== SURPRISE ? this.thumbs[choice.handle] : null;
    slot.classList.toggle('has-photo', Boolean(thumb));
    if (thumb) {
      const photo = document.createElement('span');
      photo.className = `vo__piece-photo vo__piece-photo--${slot.dataset.size}`;
      photo.append(thumb.content.cloneNode(true));
      slot.querySelector('.volier-silhouette').after(photo);
    }
    slot.querySelector('[data-slot-name]').textContent = name || 'Toca para elegir';
    slot.setAttribute(
      'aria-label',
      name ? `${slot.dataset.title}: ${name}. Toca para cambiarlo` : `${slot.dataset.title}: toca para elegir`
    );
  }

  /** First bottle of the selected combo that still has no aroma */
  #nextEmpty() {
    const combo = this.combo;
    if (!combo) return null;
    const choices = this.#choices[combo.dataset.combo];
    return [...combo.querySelectorAll('[data-slot]')].find((slot) => !choices[slot.dataset.index]) || null;
  }

  /** Progress line and button for the selected combo */
  #refresh() {
    const combo = this.combo;
    if (!combo) return;
    const slots = [...combo.querySelectorAll('[data-slot]')];
    const choices = this.#choices[combo.dataset.combo];
    const missing = slots.filter((slot) => !choices[slot.dataset.index]).length;

    this.progress.classList.toggle('is-done', missing === 0);
    this.progress.textContent =
      missing === 0
        ? '¡Listo! Tu combo está completo'
        : missing === 1
          ? 'Te falta elegir 1 aroma'
          : `Te falta elegir ${missing} aromas`;
    this.addButton.disabled = false;
    this.addButton.classList.toggle('is-pending', missing > 0);
    // The next bottle to fill gets a gentle pulse so it is obvious where to tap
    const next = this.#nextEmpty();
    this.querySelectorAll('[data-slot].is-next').forEach((slot) => slot !== next && slot.classList.remove('is-next'));
    next?.classList.add('is-next');
    this.addButton.querySelector('[data-add-label]').textContent =
      missing === 0
        ? `Agregar al carrito · ${combo.dataset.price}`
        : missing === 1
          ? 'Elige el aroma que falta'
          : `Elige los ${missing} aromas que faltan`;
    this.addError.hidden = true;
  }

  async addToCart() {
    const combo = this.combo;
    const choices = this.#choices[combo.dataset.combo];
    const properties = {};
    if (this.fixedName && combo.querySelector('[data-fixed]')) properties['Perfume 1 (100 ml)'] = this.fixedName;
    combo.querySelectorAll('[data-slot]').forEach((slot) => {
      const choice = choices[slot.dataset.index];
      properties[slot.dataset.key] = choice === SURPRISE || !choice ? GIFT_SURPRISE : choice.name;
    });

    this.addButton.setAttribute('aria-busy', 'true');
    this.addButton.disabled = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(combo.dataset.variant), quantity: 1, properties }] }),
      });
      if (!response.ok) throw new Error(await response.text());
      window.location.href = `${root}cart`;
    } catch (error) {
      console.error(error);
      this.addError.textContent = 'No pudimos agregar tu combo. Intenta de nuevo o escríbenos por WhatsApp.';
      this.addError.hidden = false;
      this.addButton.removeAttribute('aria-busy');
      this.addButton.disabled = false;
    }
  }
}

if (!customElements.get('volier-combo-box')) customElements.define('volier-combo-box', VolierComboBox);
